import { Injectable } from '@angular/core';
import {
  NormalizedQuestionType,
  RawFeedbackCategory,
  RawFeedbackQuestion,
  FeedbackCategory,
  FeedbackQuestion
} from '../interfaces/feedback.model';

@Injectable({
  providedIn: 'root'
})
export class QuestionMapperService {

  private static readonly TYPE_MAP: { [rawType: string]: NormalizedQuestionType } = {
    single_choice: 'single_choice',
    singlechoice: 'single_choice',
    radio: 'single_choice',

    multi_choice: 'multi_choice',
    multiple_choice: 'multi_choice',
    multichoice: 'multi_choice',
    checkbox: 'multi_choice',

    rating: 'rating',
    scale: 'rating',
    nps: 'rating',

    text: 'text',
    short_text: 'text',

    textarea: 'textarea',
    long_text: 'textarea',
    comment: 'textarea',

    yes_no: 'yes_no',
    yesno: 'yes_no',
    boolean: 'yes_no',

    phone: 'phone',
    tel: 'phone',

    email: 'email'
  };

  normalizeType(rawType: string | undefined | null): NormalizedQuestionType {
    if (!rawType || typeof rawType !== 'string') {
      return 'unsupported';
    }
    const key = rawType.trim().toLowerCase();
    return QuestionMapperService.TYPE_MAP[key] ?? 'unsupported';
  }

  mapQuestion(raw: RawFeedbackQuestion): FeedbackQuestion | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }

    const id = raw.question_id || raw.id;
    const questionText = raw.question;

    if (!id || !questionText) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[FeedbackConfig] Skipping malformed question (missing id/text):', raw);
      }
      return null;
    }

    const type = this.normalizeType(raw.question_type);
    if (type === 'unsupported' && typeof console !== 'undefined' && console.warn) {
      console.warn(`[FeedbackConfig] Unsupported question_type "${raw.question_type}" for question ${id} — will render a safe fallback.`);
    }

    return {
      id: String(id),
      question: String(questionText),
      type,
      options: Array.isArray(raw.options) ? raw.options.filter(o => typeof o === 'string') : [],
      required: !!raw.required,
      allowOther: !!raw.allow_other,
      otherLabel: raw.other_label && typeof raw.other_label === 'string' ? raw.other_label : 'Others (Specify)',
      displayOrder: typeof raw.display_order === 'number' ? raw.display_order : Number.MAX_SAFE_INTEGER,
      mapsTo: typeof raw.maps_to === 'string' && raw.maps_to ? raw.maps_to : null
    };
  }

  mapCategory(raw: RawFeedbackCategory, fallbackIndex: number): FeedbackCategory | null {
    if (!raw || typeof raw !== 'object' || !raw.name) {
      return null;
    }

    const rawQuestions = Array.isArray(raw.questions) ? raw.questions : [];
    const questions = rawQuestions
      .map(q => this.mapQuestion(q))
      .filter((q): q is FeedbackQuestion => q !== null)
      .sort((a, b) => a.displayOrder - b.displayOrder);

    return {
      id: raw.id ? String(raw.id) : `category-${fallbackIndex}`,
      name: raw.name,
      icon: typeof raw.icon === 'string' && raw.icon ? raw.icon : null,
      questions
    };
  }
}
